import { useContext, useState } from "react";
import {
  Typography,
  Toolbar,
  Box,
  AppBar,
  IconButton,
  MenuItem,
  Menu,
  ListItemText,
  Divider,
  ListItemIcon,
  FormControlLabel,
  styled,
  Switch,
} from "@mui/material";
import {
  BarChartOutlined,
  FilterAlt,
  InfoRounded,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "./header.scss";

import { Context } from "../../GlobalContext";

import { CELL_TOWER_ICON } from "../../utils/strings";
import { HOME, REPORT } from "../../utils/path";

import SearchCountry from "../Forms/SearchCountry/SearchCountry";

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 58,
  height: 30,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff"
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#9BB1DC",
        ...theme.applyStyles("dark", {
          backgroundColor: "#8796A5",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: "#001e3c",
    width: 28,
    height: 28,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        "#fff"
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
    ...theme.applyStyles("dark", {
      backgroundColor: "#003892",
    }),
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: "#9BB1DC",
    borderRadius: 20 / 2,
    ...theme.applyStyles("dark", {
      backgroundColor: "#8796A5",
    }),
  },
}));

export const Header = () => {
  const {
    ituDataList,
    isShownModal,
    setIsShownModal,
    isOpenedDrawer,
    setIsOpenedDrawer,
    existingData,
    darkMode,
    setDarkMode,
    setCacheCleaner,
  } = useContext(Context);
  const navigation = useNavigate();
  const [isOpenedMenu, setIsOpenedMenu] = useState(false);
  const mobileMenu = document.querySelector(".mobile-menu");
  const { pathname } = useLocation();
  const isReport = pathname === REPORT;

  const isShownButtons = ituDataList?.length > 0 && !isReport;

  const navigateToReport = () => {
    setCacheCleaner(true);

    setTimeout(() => {
      navigation(REPORT, { replace: true });
    }, 50);

    if (isOpenedMenu) {
      setIsOpenedMenu(false);
    }
  };

  const getClassName = (isActive) => {
    return `menu-button ${isActive && "activated-button"}`;
  };

  return (
    <Box className="header">
      <AppBar position="fixed">
        <Toolbar>
          <Link
            to="https://d4dhub.eu"
            target="__blank"
            className="d4d-hub-logo"
          >
            <img src="/d4d-hub-logo.png" alt="D4D hub logo" loading="lazy" />
          </Link>

          <Link
            to="https://commission.europa.eu/strategy-and-policy/priorities-2019-2024/stronger-europe-world/global-gateway_en"
            target="__blank"
            className="global-gateway-logo"
          >
            <img
              src="/global-gateway-logo.png"
              alt="global gateway logo"
              loading="lazy"
            />
          </Link>

          <Box className="header-separator first-separator" />

          <Link className="title" to={HOME} replace>
            <Typography className="title" noWrap component="h1">
              <span className="white">Broadband Demand</span>{" "}
              <span className="yellow">Mapping</span>{" "}
              <span className="light-blue">Tool</span>
            </Typography>
          </Link>

          <Link to={HOME} className="broadband-mapping-logo">
            <img
              src={CELL_TOWER_ICON}
              alt="cell tower location"
              loading="lazy"
            />
          </Link>

          <SearchCountry />

          <Box className="header-separator" />

          <Box className="header-button-wrapper">
            <Box className="mobile-menu">
              <IconButton
                aria-label="menu"
                onClick={() => setIsOpenedMenu(!isOpenedMenu)}
              >
                <MenuIcon />
              </IconButton>

              <Menu
                anchorEl={mobileMenu}
                open={isOpenedMenu}
                onClose={() => setIsOpenedMenu(false)}
                className="mobile-menu"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  className={getClassName(isShownModal)}
                  disabled={!existingData}
                  onClick={() => {
                    setIsShownModal(!isShownModal);
                    setIsOpenedMenu(false);
                  }}
                >
                  <ListItemIcon>
                    <InfoRounded fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>About</ListItemText>
                </MenuItem>

                {isShownButtons && (
                  <MenuItem
                    className={getClassName(isShownModal)}
                    disabled={!existingData}
                    onClick={navigateToReport}
                  >
                    <ListItemIcon>
                      <BarChartOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Report</ListItemText>
                  </MenuItem>
                )}

                {isShownButtons && (
                  <MenuItem
                    className={`filter-button ${getClassName(isOpenedDrawer)}`}
                    onClick={() => {
                      setIsOpenedDrawer(!isOpenedDrawer);
                      setIsOpenedMenu(false);
                    }}
                  >
                    <ListItemIcon>
                      <FilterAlt fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Filters</ListItemText>
                  </MenuItem>
                )}

                <Divider />

                <MenuItem
                  className="menu-button theme-button"
                  onClick={() => {
                    setDarkMode(!darkMode);
                  }}
                >
                  <FormControlLabel className="theme-switch" control={<MaterialUISwitch sx={{ m: 1 }} defaultChecked />} />
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Link
            to="https://european-union.europa.eu/institutions-law-budget/institutions-and-bodies/search-all-eu-institutions-and-bodies_en"
            target="__blank"
            className="eu-logo"
          >
            <img src="eu-logo.png" alt="european union logo" />
          </Link>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Header;
